import React, { useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { UserContext } from '../Contexts/UserContext';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaLock, FaGlobe, FaUsers, FaCheck, FaExclamationCircle, FaDna, FaSignInAlt, FaUserPlus } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const SignupPage = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [formData, setFormData] = useState({
        username: '',
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        ethnicity: '',
        confirmPassword: '',
        terms_and_conditions: false,
        country: '',
    });

    const [validated, setValidated] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Check if user is populated and has a token
        if (user?.token) {
            // Perform the redirect or other action if token is missing
            navigate('/');
        }
    }, [user, navigate]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckBoxChange = () => {
        setFormData({
            ...formData,
            terms_and_conditions: !formData.terms_and_conditions
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        if (form.checkValidity() === false) {
            e.stopPropagation();
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return
        }

        if (formData.terms_and_conditions !== true) {
            setError('Terms and Conditions Are Required');
            return
        }

        setValidated(true);
        setError('')

        const payload = {
            first_name: formData.firstname,
            last_name: formData.lastname,
            username: formData.username,
            email_address: formData.email,
            role: "5d3bfbda-3169-4747-babc-e7642fe4bd50",
            subscription_plan: "65174cb8-42f4-4554-ae30-97fadcf827a3",
            password: formData.password,
            ethnicity: formData.ethnicity,
            country: formData.country,
            terms_and_conditions: formData.terms_and_conditions,
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}users/users`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            console.log('Network response was not ok');
        }

        const result = await response.json();

        if (result.uuid) {
            navigate('/signup_success');
        }
    };

    const styles = {
        pageContainer: {
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            padding: '2rem 0',
        },
        card: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: '#ffffff',
            marginBottom: '2rem',
        },
        header: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            padding: '2rem',
            color: 'white',
            textAlign: 'center',
            borderBottom: 'none',
        },
        logo: {
            fontSize: '2.5rem',
            marginBottom: '1rem',
            color: '#ffffff',
        },
        headerTitle: {
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '0.5rem',
        },
        headerSubtitle: {
            fontSize: '1rem',
            opacity: '0.9',
        },
        formContainer: {
            padding: '2rem',
        },
        formGroup: {
            marginBottom: '1.5rem',
        },
        formLabel: {
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#4a5568',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        formControl: {
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.75rem',
            fontSize: '1rem',
            transition: 'all 0.2s ease',
            backgroundColor: '#f8fafc',
            '&:focus': {
                borderColor: '#2C5282',
                boxShadow: '0 0 0 3px rgba(44,82,130,0.1)',
            },
        },
        submitButton: {
            background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontWeight: '600',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            transition: 'transform 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
            },
        },
        loginLink: {
            textAlign: 'center',
            marginTop: '1rem',
            color: '#4a5568',
        },
        loginButton: {
            color: '#2C5282',
            textDecoration: 'none',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'color 0.2s ease',
            '&:hover': {
                color: '#1a365d',
            },
        },
        alert: {
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        icon: {
            fontSize: '1.2rem',
        },
        termsContainer: {
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
        },
        termsLink: {
            color: '#2C5282',
            textDecoration: 'none',
            fontWeight: '500',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
    };

    return (
        <>
            <Helmet>
                <title>DNACloudHub | Sign Up</title>
                <meta name="description" content="Sign up for DNACloudHub to get started with our cloud-based platform for running qpAdm models and performing advanced genetic analysis. Access powerful tools with ease and flexibility." />
                <meta property="og:title" content={'DNACloudHub | Sign Up'} />
                <meta property="og:description" content={'"Sign up for DNACloudHub to get started with our cloud-based platform for running qpAdm models and performing advanced genetic analysis. Access powerful tools with ease and flexibility."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/signup`} />
            </Helmet>

            <div style={styles.pageContainer}>
                <Container>
                    <Row className="justify-content-center">
                        <Col md={8} lg={7}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <Card style={styles.card}>
                                    <div style={styles.header}>
                                        <FaDna style={styles.logo} />
                                        <h2 style={styles.headerTitle}>Create Account</h2>
                                        <p style={styles.headerSubtitle}>
                                            Join DNACloudHub and start exploring
                                        </p>
                                    </div>

                                    <div style={styles.formContainer}>
                                        {error && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <Alert variant="danger" style={styles.alert}>
                                                    <FaExclamationCircle style={styles.icon} />
                                                    {error}
                                                </Alert>
                                            </motion.div>
                                        )}

                                        <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.1 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formUsername">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaUser style={styles.icon} /> Username
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="text"
                                                                name="username"
                                                                placeholder="Choose a username"
                                                                value={formData.username}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.2 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formEmail">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaEnvelope style={styles.icon} /> Email
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="email"
                                                                name="email"
                                                                placeholder="Enter your email"
                                                                value={formData.email}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.3 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formFirstname">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaUser style={styles.icon} /> First Name
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="text"
                                                                name="firstname"
                                                                placeholder="Enter your first name"
                                                                value={formData.firstname}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.4 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formLastname">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaUser style={styles.icon} /> Last Name
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="text"
                                                                name="lastname"
                                                                placeholder="Enter your last name"
                                                                value={formData.lastname}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.5 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formCountry">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaGlobe style={styles.icon} /> Country
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="text"
                                                                name="country"
                                                                placeholder="Enter your country"
                                                                value={formData.country}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.6 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formEthnicity">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaUsers style={styles.icon} /> Ethnicity
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="text"
                                                                name="ethnicity"
                                                                placeholder="Enter your ethnicity"
                                                                value={formData.ethnicity}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.7 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formPassword">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaLock style={styles.icon} /> Password
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="password"
                                                                name="password"
                                                                placeholder="Create a password"
                                                                value={formData.password}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                                <Col md={6}>
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: 0.8 }}
                                                    >
                                                        <Form.Group className="mb-4" controlId="formConfirmPassword">
                                                            <Form.Label style={styles.formLabel}>
                                                                <FaLock style={styles.icon} /> Confirm Password
                                                            </Form.Label>
                                                            <Form.Control
                                                                required
                                                                type="password"
                                                                name="confirmPassword"
                                                                placeholder="Confirm your password"
                                                                value={formData.confirmPassword}
                                                                onChange={handleChange}
                                                                style={styles.formControl}
                                                            />
                                                        </Form.Group>
                                                    </motion.div>
                                                </Col>
                                            </Row>

                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.3, delay: 0.9 }}
                                                style={styles.termsContainer}
                                            >
                                                <Form.Group controlId="formTermsAndConditions">
                                                    <Form.Check
                                                        required
                                                        type="checkbox"
                                                        name="formTermsAndConditions"
                                                        label={
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                <FaCheck style={{ ...styles.icon, color: formData.terms_and_conditions ? '#2C5282' : '#cbd5e0' }} />
                                                                I agree to the{' '}
                                                                <a href="/terms_and_conditions" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>
                                                                    Terms and Conditions
                                                                </a>{' '}
                                                                and{' '}
                                                                <a href="/privacy_policy" target="_blank" rel="noopener noreferrer" style={styles.termsLink}>
                                                                    Privacy Policy
                                                                </a>
                                                            </span>
                                                        }
                                                        value={formData.terms_and_conditions}
                                                        onChange={handleCheckBoxChange}
                                                    />
                                                </Form.Group>
                                            </motion.div>

                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3, delay: 1.0 }}
                                            >
                                                <Button
                                                    variant="primary"
                                                    type="submit"
                                                    style={styles.submitButton}
                                                >
                                                    <FaUserPlus style={styles.icon} /> Create Account
                                                </Button>
                                            </motion.div>
                                        </Form>

                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.3, delay: 1.1 }}
                                            style={styles.loginLink}
                                        >
                                            Already have an account?{' '}
                                            <Link to="/login" style={styles.loginButton}>
                                                <FaSignInAlt style={styles.icon} /> Sign in here
                                            </Link>
                                        </motion.div>
                                    </div>
                                </Card>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default SignupPage;
